import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabaseClient'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'

const CATEGORIES = [
  'Pitch Emails',
  'Cold Intro Emails',
  'Bio',
  'Outlines',
  'Social Media',
  'LinkedIn Profile',
  'Contracts',
  'Other',
]

// ─── Rich text display (read-only) ───────────────────────────────────────────
function TemplateViewer({ content, theme }) {
  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: true })],
    content,
    editable: false,
  })

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content)
    }
  }, [content, editor])

  return (
    <div style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 14, color: theme.text, lineHeight: 1.7 }}>
      <style>{`
        .template-viewer h2 { font-size: 18px; font-family: 'Cardo', serif; color: ${theme.text}; margin: 16px 0 8px; }
        .template-viewer h3 { font-size: 15px; font-family: 'Cardo', serif; color: ${theme.text}; margin: 14px 0 6px; }
        .template-viewer p { margin: 0 0 10px; }
        .template-viewer ul, .template-viewer ol { padding-left: 20px; margin: 0 0 10px; }
        .template-viewer li { margin-bottom: 4px; }
        .template-viewer strong { font-weight: 700; color: ${theme.text}; }
        .template-viewer em { font-style: italic; }
        .template-viewer a { color: ${theme.accent}; text-decoration: underline; }
        .template-viewer .ProseMirror { outline: none; }
      `}</style>
      <div className="template-viewer">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}

// ─── Rich text editor (write) ─────────────────────────────────────────────────
function EditorToolbar({ editor, theme }) {
  if (!editor) return null

  const btn = (action, label, active) => (
    <button
      onMouseDown={e => { e.preventDefault(); action() }}
      style={{
        background: active ? `${theme.accent}22` : 'transparent',
        border: `1px solid ${active ? theme.accent : theme.border}`,
        color: active ? theme.accent : theme.textSecondary,
        padding: '3px 9px', fontSize: 12, cursor: 'pointer',
        fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.04em',
      }}
    >{label}</button>
  )

  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '8px 12px', background: theme.surfaceAlt, borderBottom: `1px solid ${theme.border}` }}>
      {btn(() => editor.chain().focus().toggleBold().run(), 'B', editor.isActive('bold'))}
      {btn(() => editor.chain().focus().toggleItalic().run(), 'I', editor.isActive('italic'))}
      {btn(() => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'H2', editor.isActive('heading', { level: 2 }))}
      {btn(() => editor.chain().focus().toggleHeading({ level: 3 }).run(), 'H3', editor.isActive('heading', { level: 3 }))}
      {btn(() => editor.chain().focus().toggleBulletList().run(), '• List', editor.isActive('bulletList'))}
      {btn(() => editor.chain().focus().toggleOrderedList().run(), '1. List', editor.isActive('orderedList'))}
      {btn(() => {
        const url = window.prompt('Enter URL')
        if (url) editor.chain().focus().setLink({ href: url }).run()
      }, 'Link', editor.isActive('link'))}
      {editor.isActive('link') && btn(() => editor.chain().focus().unsetLink().run(), 'Unlink', false)}
    </div>
  )
}

function TemplateEditor({ initialContent, onChange, theme }) {
  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: false })],
    content: initialContent || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  })

  return (
    <div style={{ border: `1px solid ${theme.border}`, background: theme.surface }}>
      <EditorToolbar editor={editor} theme={theme} />
      <style>{`
        .template-write-editor h2 { font-size: 18px; font-family: 'Cardo', serif; color: ${theme.text}; margin: 16px 0 8px; }
        .template-write-editor h3 { font-size: 15px; font-family: 'Cardo', serif; color: ${theme.text}; margin: 14px 0 6px; }
        .template-write-editor p { margin: 0 0 10px; }
        .template-write-editor ul, .template-write-editor ol { padding-left: 20px; margin: 0 0 10px; }
        .template-write-editor li { margin-bottom: 4px; }
        .template-write-editor a { color: ${theme.accent}; }
        .template-write-editor .ProseMirror { outline: none; min-height: 200px; padding: 14px 16px; color: ${theme.text}; font-size: 14px; font-family: 'Nunito Sans', sans-serif; line-height: 1.7; }
        .template-write-editor .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: ${theme.textMuted}; pointer-events: none; float: left; height: 0; }
      `}</style>
      <div className="template-write-editor">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}

// ─── Copy button with plain-text extraction ───────────────────────────────────
function htmlToPlainText(html) {
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  // Preserve line breaks for block elements
  tmp.querySelectorAll('p, h2, h3, li, br').forEach(el => {
    if (el.tagName === 'BR') {
      el.replaceWith('\n')
    } else {
      el.insertAdjacentText('afterend', '\n')
    }
  })
  // Add extra line break after headings
  tmp.querySelectorAll('h2, h3').forEach(el => el.insertAdjacentText('afterend', '\n'))
  return tmp.textContent.replace(/\n{3,}/g, '\n\n').trim()
}

function CopyButton({ content, templateId, isLibrary, theme, onCopied }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    const plain = htmlToPlainText(content)
    await navigator.clipboard.writeText(plain)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    onCopied(templateId, isLibrary)
  }

  return (
    <button onClick={handleCopy} style={{
      background: copied ? `${theme.accent}22` : theme.accent,
      color: copied ? theme.accent : '#0a2342',
      border: copied ? `1px solid ${theme.accent}` : 'none',
      padding: '8px 20px', fontSize: 11, letterSpacing: '0.1em',
      textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif",
      fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
    }}>
      {copied ? '✓ Copied' : 'Copy Template'}
    </button>
  )
}

// ─── Template card ────────────────────────────────────────────────────────────
function TemplateCard({ template, theme, isLibrary, onCopied, onEdit, onDelete, canEdit }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, marginBottom: 10 }}>
      {/* Header row */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: '16px 20px', cursor: 'pointer', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          transition: 'background 0.1s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = theme.surfaceAlt}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, color: theme.text, fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600, marginBottom: 4 }}>
            {template.title}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{
              fontSize: 10, color: theme.accent, background: `${theme.accent}18`,
              padding: '2px 8px', fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.08em',
            }}>{template.category}</span>
            <span style={{ fontSize: 11, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif" }}>
              {isLibrary
                ? `Used ${template.use_count} times by writers`
                : `Copied ${template.use_count} time${template.use_count !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginLeft: 16 }}>
          {expanded ? '▲' : '▼'}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${theme.border}` }}>
          <div style={{ padding: '20px 24px', background: theme.surfaceAlt }}>
            <TemplateViewer content={template.content} theme={theme} />
          </div>
          <div style={{ padding: '14px 20px', display: 'flex', gap: 8, alignItems: 'center', borderTop: `1px solid ${theme.border}` }}>
            <CopyButton
              content={template.content}
              templateId={template.id}
              isLibrary={isLibrary}
              theme={theme}
              onCopied={onCopied}
            />
            {!isLibrary && canEdit && (
              <>
                <button onClick={() => onEdit(template)} style={{
                  background: 'transparent', border: `1px solid ${theme.border}`,
                  color: theme.textSecondary, padding: '7px 16px', fontSize: 11,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  fontFamily: "'Open Sans', sans-serif", cursor: 'pointer',
                }}>Edit</button>
                <button onClick={() => onDelete(template.id)} style={{
                  background: 'transparent', border: `1px solid ${theme.border}`,
                  color: '#ef4444', padding: '7px 16px', fontSize: 11,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  fontFamily: "'Open Sans', sans-serif", cursor: 'pointer',
                }}>Delete</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── User template form ───────────────────────────────────────────────────────
function UserTemplateForm({ initial, userTags, theme, onSave, onCancel }) {
  const [title, setTitle] = useState(initial?.title || '')
  const [category, setCategory] = useState(initial?.category || CATEGORIES[0])
  const [content, setContent] = useState(initial?.content || '')
  const [newTag, setNewTag] = useState('')
  const [saving, setSaving] = useState(false)

  const allCategories = [...CATEGORIES, ...userTags.filter(t => !CATEGORIES.includes(t))]

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) return
    setSaving(true)
    await onSave({ title: title.trim(), category, content }, newTag.trim())
    setSaving(false)
  }

  const inputStyle = {
    width: '100%', background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
    color: theme.text, padding: '9px 12px', fontSize: 13,
    fontFamily: "'Nunito Sans', sans-serif", boxSizing: 'border-box', outline: 'none',
  }

  const labelStyle = {
    fontSize: 10, color: theme.textSecondary, marginBottom: 5,
    letterSpacing: '0.1em', fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase',
  }

  return (
    <div style={{ background: theme.surface, border: `1px solid ${theme.accent}44`, padding: 24, marginBottom: 20 }}>
      <div style={{ fontSize: 11, color: theme.accent, letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: "'Open Sans', sans-serif", marginBottom: 20 }}>
        {initial ? 'Edit Template' : 'New Template'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div>
          <div style={labelStyle}>Title *</div>
          <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. My Cold Pitch" />
        </div>
        <div>
          <div style={labelStyle}>Category</div>
          <select style={inputStyle} value={category} onChange={e => setCategory(e.target.value)}>
            {allCategories.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <div style={labelStyle}>Or create a new tag</div>
          <input
            style={inputStyle}
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            placeholder="e.g. My Niche Templates"
          />
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4, fontFamily: "'Nunito Sans', sans-serif" }}>
            If filled, this tag will be saved and used instead of the category above.
          </div>
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={labelStyle}>Content *</div>
        <TemplateEditor initialContent={initial?.content || ''} onChange={setContent} theme={theme} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleSubmit} disabled={saving || !title.trim() || !content.trim()} style={{
          background: title.trim() && content.trim() ? theme.accent : theme.border,
          color: '#0a2342', border: 'none', padding: '9px 22px', fontSize: 11,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          fontFamily: "'Open Sans', sans-serif", fontWeight: 700,
          cursor: title.trim() && content.trim() ? 'pointer' : 'not-allowed',
        }}>{saving ? 'Saving...' : (initial ? 'Update Template' : 'Save Template')}</button>
        <button onClick={onCancel} style={{
          background: 'transparent', border: `1px solid ${theme.border}`,
          color: theme.textMuted, padding: '9px 22px', fontSize: 11,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          fontFamily: "'Open Sans', sans-serif", cursor: 'pointer',
        }}>Cancel</button>
      </div>
    </div>
  )
}

// ─── Main Templates component ─────────────────────────────────────────────────
export default function Templates({ session, theme, canEdit = true }) {
  const [tab, setTab] = useState('library')
  const [libraryTemplates, setLibraryTemplates] = useState([])
  const [userTemplates, setUserTemplates] = useState([])
  const [userTags, setUserTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [filterCategory, setFilterCategory] = useState('All')
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (session?.user?.id) {
      fetchAll()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  const fetchAll = async () => {
    setLoading(true)
    const [libRes, userRes, tagsRes] = await Promise.all([
      supabase.from('library_templates').select('*').order('category').order('title'),
      supabase.from('user_templates').select('*').eq('user_id', session.user.id).order('created_at', { ascending: false }),
      supabase.from('user_template_tags').select('name').eq('user_id', session.user.id).order('name'),
    ])
    if (libRes.data) setLibraryTemplates(libRes.data)
    if (userRes.data) setUserTemplates(userRes.data)
    if (tagsRes.data) setUserTags(tagsRes.data.map(t => t.name))
    setLoading(false)
  }

  // Increment use_count when a template is copied
  const handleCopied = useCallback(async (id, isLibrary) => {
    const table = isLibrary ? 'library_templates' : 'user_templates'
    const templates = isLibrary ? libraryTemplates : userTemplates
    const template = templates.find(t => t.id === id)
    if (!template) return
    await supabase.from(table).update({ use_count: template.use_count + 1 }).eq('id', id)
    if (isLibrary) {
      setLibraryTemplates(prev => prev.map(t => t.id === id ? { ...t, use_count: t.use_count + 1 } : t))
    } else {
      setUserTemplates(prev => prev.map(t => t.id === id ? { ...t, use_count: t.use_count + 1 } : t))
    }
  }, [libraryTemplates, userTemplates])

  const handleSaveUserTemplate = async ({ title, category, content }, newTag) => {
    const finalCategory = newTag || category
    if (editingTemplate) {
      const { error } = await supabase.from('user_templates')
        .update({ title, category: finalCategory, content, updated_at: new Date().toISOString() })
        .eq('id', editingTemplate.id)
      if (!error) {
        if (newTag) await supabase.from('user_template_tags')
          .upsert({ user_id: session.user.id, name: newTag }, { onConflict: 'user_id,name' })
        setEditingTemplate(null)
        fetchAll()
      }
    } else {
      const { error } = await supabase.from('user_templates').insert([{
        user_id: session.user.id, title, category: finalCategory, content, use_count: 0,
      }])
      if (!error) {
        if (newTag) await supabase.from('user_template_tags')
          .upsert({ user_id: session.user.id, name: newTag }, { onConflict: 'user_id,name' })
        setShowForm(false)
        fetchAll()
      }
    }
  }

  const handleDeleteUserTemplate = async (id) => {
    if (deleteConfirm !== id) { setDeleteConfirm(id); return }
    await supabase.from('user_templates').delete().eq('id', id)
    setDeleteConfirm(null)
    fetchAll()
  }

  const filterTemplates = (templates) => {
    return templates.filter(t => {
      const matchCat = filterCategory === 'All' || t.category === filterCategory
      const matchSearch = !search || t.title.toLowerCase().includes(search.toLowerCase()) || t.content.toLowerCase().includes(search.toLowerCase())
      return matchCat && matchSearch
    })
  }

  const activeTemplates = tab === 'library' ? filterTemplates(libraryTemplates) : filterTemplates(userTemplates)
  const allUserCategories = [...CATEGORIES, ...userTags.filter(t => !CATEGORIES.includes(t))]
  const filterCategories = tab === 'library' ? CATEGORIES : allUserCategories

  const tabStyle = (id) => ({
    background: 'transparent', border: 'none', borderBottom: tab === id ? `2px solid ${theme.accent}` : '2px solid transparent',
    color: tab === id ? theme.accent : theme.textMuted,
    padding: '10px 20px', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
    fontFamily: "'Open Sans', sans-serif", fontWeight: tab === id ? 700 : 400,
    cursor: 'pointer', marginBottom: -1,
  })

  return (
    <div>
      {/* Tabs */}
      <div style={{ borderBottom: `1px solid ${theme.border}`, marginBottom: 24, display: 'flex', gap: 0 }}>
        <button style={tabStyle('library')} onClick={() => { setTab('library'); setFilterCategory('All'); setSearch('') }}>
          Library ({libraryTemplates.length})
        </button>
        <button style={tabStyle('mine')} onClick={() => { setTab('mine'); setFilterCategory('All'); setSearch('') }}>
          My Templates ({userTemplates.length})
        </button>
      </div>

      {/* My Templates — add/edit form */}
      {tab === 'mine' && !editingTemplate && !showForm && canEdit && (
        <button onClick={() => setShowForm(true)} style={{
          background: theme.accent, color: '#0a2342', border: 'none',
          padding: '8px 18px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
          fontFamily: "'Open Sans', sans-serif", fontWeight: 700, cursor: 'pointer', marginBottom: 20,
        }}>+ New Template</button>
      )}

      {tab === 'mine' && showForm && (
        <UserTemplateForm
          userTags={userTags}
          theme={theme}
          onSave={handleSaveUserTemplate}
          onCancel={() => setShowForm(false)}
        />
      )}

      {tab === 'mine' && editingTemplate && (
        <UserTemplateForm
          initial={editingTemplate}
          userTags={userTags}
          theme={theme}
          onSave={handleSaveUserTemplate}
          onCancel={() => setEditingTemplate(null)}
        />
      )}

      {/* Search + filter */}
      {!showForm && !editingTemplate && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search templates..."
            style={{
              background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
              color: theme.text, padding: '6px 12px', fontSize: 12,
              fontFamily: "'Nunito Sans', sans-serif", outline: 'none', width: 220,
            }}
          />
          {['All', ...filterCategories].map(c => (
            <button key={c} onClick={() => setFilterCategory(c)} style={{
              background: filterCategory === c ? theme.accent : theme.surface,
              color: filterCategory === c ? '#0a2342' : theme.textSecondary,
              border: `1px solid ${filterCategory === c ? theme.accent : theme.border}`,
              padding: '5px 12px', fontSize: 11, cursor: 'pointer',
              fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.06em',
              fontWeight: filterCategory === c ? 700 : 400,
            }}>{c}</button>
          ))}
        </div>
      )}

      {/* Results count */}
      {!showForm && !editingTemplate && (
        <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 12, fontFamily: "'Open Sans', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {activeTemplates.length} {activeTemplates.length === 1 ? 'template' : 'templates'}
          {filterCategory !== 'All' && <span style={{ color: theme.accent, marginLeft: 6 }}>· {filterCategory}</span>}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ padding: '32px', textAlign: 'center', color: theme.textMuted, fontSize: 13, fontFamily: "'Nunito Sans', sans-serif" }}>
          Loading templates...
        </div>
      )}

      {/* Empty state */}
      {!loading && !showForm && !editingTemplate && activeTemplates.length === 0 && (
        <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", marginBottom: 8 }}>
            {tab === 'library' ? 'No templates in the library yet' : 'No templates yet'}
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: "'Nunito Sans', sans-serif", opacity: 0.6 }}>
            {tab === 'library' ? 'Check back soon — Ravenwood templates are on the way' : 'Click "+ New Template" to add your first one'}
          </div>
        </div>
      )}

      {/* Template list */}
      {!loading && !showForm && !editingTemplate && activeTemplates.map(t => (
        <TemplateCard
          key={t.id}
          template={deleteConfirm === t.id ? { ...t, _confirmDelete: true } : t}
          theme={theme}
          isLibrary={tab === 'library'}
          canEdit={canEdit}
          onCopied={handleCopied}
          onEdit={(template) => { setEditingTemplate(template); window.scrollTo(0, 0) }}
          onDelete={handleDeleteUserTemplate}
        />
      ))}

      {/* Delete confirm hint */}
      {deleteConfirm && (
        <div style={{ fontSize: 11, color: '#ef4444', fontFamily: "'Nunito Sans', sans-serif", marginTop: 8 }}>
          Click Delete again on that template to confirm.
        </div>
      )}
    </div>
  )
}